import { useState, useEffect, useRef, useCallback } from 'react';

const DEFAULT_IDLE_TIMEOUT_MIN = 30;
const WARNING_COUNTDOWN_S = 60;
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'pointerdown'];
const THROTTLE_MS = 15_000;
const CHECK_INTERVAL_MS = 10_000;

function decodeJwtExp(token) {
  if (!token) return null;
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    return typeof payload.exp === 'number' ? payload.exp : null;
  } catch {
    return null;
  }
}

/**
 * Hook de gestion du timeout de session.
 *
 * @param {object} opts
 * @param {boolean} opts.isAuthenticated
 * @param {string|null} opts.token
 * @param {number} [opts.timeoutMinutes] – durée d'inactivité en minutes (depuis la console admin)
 * @param {() => void} opts.onLogout
 * @returns {{ showWarning: boolean, countdown: number, dismissWarning: () => void }}
 */
export function useSessionTimeout({ isAuthenticated, token, timeoutMinutes, onLogout }) {
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(WARNING_COUNTDOWN_S);

  const lastActivityRef = useRef(Date.now());
  const warningActiveRef = useRef(false);
  const throttleRef = useRef(0);
  const onLogoutRef = useRef(onLogout);
  const tokenRef = useRef(token);

  const idleTimeoutMs = (
    typeof timeoutMinutes === 'number' && timeoutMinutes >= 1
      ? timeoutMinutes
      : DEFAULT_IDLE_TIMEOUT_MIN
  ) * 60 * 1000;

  useEffect(() => { onLogoutRef.current = onLogout; }, [onLogout]);
  useEffect(() => { tokenRef.current = token; }, [token]);

  const dismissWarning = useCallback(() => {
    warningActiveRef.current = false;
    setShowWarning(false);
    setCountdown(WARNING_COUNTDOWN_S);
    lastActivityRef.current = Date.now();
  }, []);

  // Idle detection
  useEffect(() => {
    if (!isAuthenticated) {
      warningActiveRef.current = false;
      setShowWarning(false);
      return;
    }

    lastActivityRef.current = Date.now();

    const handleActivity = () => {
      const now = Date.now();
      if (now - throttleRef.current < THROTTLE_MS) return;
      throttleRef.current = now;
      lastActivityRef.current = now;
    };

    ACTIVITY_EVENTS.forEach((evt) => {
      document.addEventListener(evt, handleActivity, { passive: true });
    });

    const intervalId = setInterval(() => {
      if (warningActiveRef.current) return;

      const tk = tokenRef.current;
      const exp = decodeJwtExp(tk);
      if (exp) {
        const nowSec = Math.floor(Date.now() / 1000);
        if (nowSec >= exp) {
          onLogoutRef.current();
          return;
        }
        if (exp - nowSec <= WARNING_COUNTDOWN_S + 10) {
          warningActiveRef.current = true;
          setShowWarning(true);
          setCountdown(WARNING_COUNTDOWN_S);
          return;
        }
      }

      const idle = Date.now() - lastActivityRef.current;
      if (idle >= idleTimeoutMs) {
        warningActiveRef.current = true;
        setShowWarning(true);
        setCountdown(WARNING_COUNTDOWN_S);
      }
    }, CHECK_INTERVAL_MS);

    return () => {
      ACTIVITY_EVENTS.forEach((evt) => {
        document.removeEventListener(evt, handleActivity);
      });
      clearInterval(intervalId);
    };
  }, [isAuthenticated, idleTimeoutMs]);

  // Countdown — separate effect
  useEffect(() => {
    if (!showWarning) return;

    const countdownId = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownId);
          onLogoutRef.current();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(countdownId);
  }, [showWarning]);

  return { showWarning, countdown, dismissWarning };
}
